import { FunctionComponent, useState, useEffect, useCallback } from 'react';

// Hooks & Helpers
import SafeLocalStorage from '~/helper/SafeLocalStorage';
import useElementViewed from '~/lib/segment/useElementViewed';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useRouter } from 'next/router';
import { AnimationStyles } from './AnimationStyles';

// Components
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';

// Data
import { DEFAULT_PROPS, STUDENT_PROPS } from './data';

// Classes
import * as classes from './hoveringCTAClasses';
const { ctaFadeIn, ctaFadeOut, ctaClasses } = classes;

interface HoveringStickyMobileCTAProps {
  CTAButtonText?: string;
  CTAButtonVariant?: CTAColorVariant;
  CTAButtonLink?: string;
}

const HoveringStickyMobileCTA: FunctionComponent<
  HoveringStickyMobileCTAProps
> = (props) => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const router = useRouter();
  const isStudentPage = router.pathname.startsWith('/student');
  const baseProps = isStudentPage ? STUDENT_PROPS : DEFAULT_PROPS;
  const mergedData = {
    ...baseProps,
    ...data,
    ...props,
  };
  const { CTAButtonText, CTAButtonVariant, CTAButtonLink } = mergedData;

  const [isVisible, setIsVisible] = useState(false);

  // Create memoized scroll handler
  const handleScroll = useCallback(() => {
    const scrollElement = document.getElementById('trigger-hov-cta');

    if (scrollElement) {
      const belowHeroPosition = scrollElement.getBoundingClientRect();
      const scrollPosition = window.innerHeight;

      // Null-safe condition to prevent potential error on bottom property
      if (
        belowHeroPosition &&
        typeof belowHeroPosition.bottom === 'number' &&
        belowHeroPosition.bottom <= scrollPosition
      ) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }
  }, []);

  const handleLoad = useCallback(() => {
    const scrollPosition: number | undefined =
      SafeLocalStorage.getItem('scrollPosition');
    if (scrollPosition && scrollPosition >= 700) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, []);

  // Create memoized before unload handler
  const handleBeforeUnload = useCallback(() => {
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;
    SafeLocalStorage.setItem('scrollPosition', scrollPosition);
  }, []);

  // Scroll event listener management
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Load event listeners with cleanup
  useEffect(() => {
    window.addEventListener('load', handleLoad);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleLoad, handleBeforeUnload]);

  const hoverCTARef = useElementViewed();

  // Track when element becomes visible for the first time
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (isVisible && !hasBeenVisible) {
      setHasBeenVisible(true);
    }
  }, [isVisible, hasBeenVisible]);

  return (
    <>
      <style>{AnimationStyles}</style>
      <div className={isVisible ? ctaFadeIn : ctaFadeOut}>
        {isVisible && (
          <span
            ref={hasBeenVisible ? hoverCTARef : null}
            data-element-position='BOTTOM_STICKY_SECTION_IN_VIEW'
            data-element-name='HOVER_CTA'
          >
            <CTAButton
              className={ctaClasses}
              variant={CTAButtonVariant}
              size='aa'
              elementPosition='STICKY_FOOTER_SIGNUP_CTA'
              elementType='SIGN_UP_CTA_PRIMARY'
              href={CTAButtonLink}
            >
              {CTAButtonText}
            </CTAButton>
          </span>
        )}
      </div>
    </>
  );
};

export default HoveringStickyMobileCTA;
